import React from 'react';
import './LinkifiedText.scss';

/**
 * URL regex pattern that matches http:// and https:// URLs.
 * Uses capturing group so split() retains the matched URLs in the result array.
 * Excludes dangerous/special characters for security.
 */
const URL_PATTERN = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/gi;

/**
 * Simple regex to test if a string starts with http:// or https://
 * Used to identify URL parts after splitting (avoids lastIndex issues with global regex)
 */
const URL_TEST_PATTERN = /^https?:\/\//i;

/**
 * Truncates a URL for display while preserving the domain for security/trust.
 * Uses the native URL API for reliable parsing.
 *
 * @param {string} url - The full URL to truncate
 * @param {number} maxLength - Maximum display length (default: 50)
 * @returns {string} Truncated URL suitable for display
 *
 * @example
 * truncateUrl("https://checkout.stripe.com/c/pay/cs_test_abc123xyz", 40)
 * // Returns: "checkout.stripe.com/c/pay/..."
 */
function truncateUrl(url, maxLength = 50) {
  // If URL is already short enough, just remove protocol for cleaner display
  if (url.length <= maxLength) {
    return url.replace(/^https?:\/\//, '');
  }

  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname;
    const path = urlObj.pathname;
    const search = urlObj.search;
    const hash = urlObj.hash;

    // Start with domain (always show full domain for security)
    let display = domain;

    // Calculate remaining space for path
    const remainingLength = maxLength - domain.length;

    // If there's a meaningful path (not just "/")
    if (path && path !== '/') {
      const fullPath = path + search + hash;

      if (remainingLength > 4) {
        // Room for at least "/..."
        if (fullPath.length <= remainingLength) {
          // Full path fits
          display += fullPath;
        } else {
          // Truncate path intelligently
          display += fullPath.substring(0, remainingLength - 3) + '...';
        }
      } else {
        // Very little room, just indicate there's more
        display += '/...';
      }
    }

    return display;
  } catch (e) {
    // Fallback for malformed URLs: simple truncation
    const withoutProtocol = url.replace(/^https?:\/\//, '');
    if (withoutProtocol.length <= maxLength) {
      return withoutProtocol;
    }
    return withoutProtocol.substring(0, maxLength - 3) + '...';
  }
}

/**
 * LinkifiedText - Renders text with URLs automatically converted to clickable links
 *
 * @param {Object} props - Component props
 * @param {string} props.text - The text content that may contain URLs
 * @param {string} [props.className] - Optional CSS class for the container span
 * @param {number} [props.maxUrlLength=50] - Maximum length for displayed URL text
 * @param {boolean} [props.showFullUrlOnHover=true] - Whether to show full URL in tooltip on hover
 * @returns {JSX.Element|string|null} Text with URLs rendered as clickable links
 *
 * @example
 * <LinkifiedText text="Visit https://example.com for more info" />
 * // Renders: Visit <a href="https://example.com" title="https://example.com">example.com</a> for more info
 */
export function LinkifiedText({
  text,
  className,
  maxUrlLength = 50,
  showFullUrlOnHover = true,
}) {
  // Handle edge cases: null, undefined, non-string values
  if (!text || typeof text !== 'string') {
    return text ?? null;
  }

  // Split text by URLs while keeping the URLs in the result array
  // Example: "Hello https://example.com world" → ["Hello ", "https://example.com", " world"]
  const parts = text.split(URL_PATTERN);

  // If no URLs found, parts will have length 1 (just the original text)
  // Optimization: skip processing if no URLs detected
  if (parts.length === 1) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>
      {parts.map((part, index) => {
        // Skip empty strings that can result from split
        if (!part) return null;

        // Check if this part is a URL using the test pattern
        const isUrl = URL_TEST_PATTERN.test(part);

        if (isUrl) {
          // Truncate URL for cleaner display
          const displayUrl = truncateUrl(part, maxUrlLength);

          return (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="linkified-url"
              onClick={(e) => e.stopPropagation()}
              // Show full URL on hover via native tooltip
              title={showFullUrlOnHover ? part : undefined}
              // Accessibility: describe the link action
              aria-label="Open link in new tab"
            >
              {displayUrl}
            </a>
          );
        }

        // Return plain text parts wrapped in React.Fragment with key
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </span>
  );
}

export default LinkifiedText;
